import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Volume2, VolumeX } from 'lucide-react';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Unmute on the first GENUINE user gesture (click, tap, or keypress)
  // Note: mousemove/scroll do NOT count as valid gestures for browser audio policies
  useEffect(() => {
    const unmuteOnInteraction = () => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        videoRef.current.play().catch(() => {
          // If play() is still rejected, leave muted as-is
          videoRef.current!.muted = true;
          setIsMuted(true);
          return;
        });
        setIsMuted(false);
      }
      window.removeEventListener('click', unmuteOnInteraction);
      window.removeEventListener('touchend', unmuteOnInteraction);
      window.removeEventListener('keydown', unmuteOnInteraction);
    };

    window.addEventListener('click', unmuteOnInteraction);
    window.addEventListener('touchend', unmuteOnInteraction);
    window.addEventListener('keydown', unmuteOnInteraction);

    return () => {
      window.removeEventListener('click', unmuteOnInteraction);
      window.removeEventListener('touchend', unmuteOnInteraction);
      window.removeEventListener('keydown', unmuteOnInteraction);
    };
  }, []);

  const toggleSound = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      if (!newMuted) videoRef.current.play().catch(() => {});
      setIsMuted(newMuted);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Background Image */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{
            minWidth: '100%',
            minHeight: '100%',
          }}
        >
          <source src="/Alebel Home Video long (3).mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-navy/20 via-navy/10 to-navy/90"></div>

        {/* Sound toggle - simple, visible, always clickable */}
        <button
          onClick={toggleSound}
          className="absolute top-20 md:top-40 left-1/2 -translate-x-1/2 z-30 flex items-center justify-center w-14 h-14 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-all duration-300"          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
        >
          {isMuted ? <VolumeX size={22} /> : <Volume2 size={22} />}
        </button>
      </div>
      
      {/* Moving Color Overlay */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-gold/10 animate-gradient"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gold/5 to-transparent animate-gradient" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.2) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(26, 58, 92, 0.2) 0%, transparent 50%)`,
        }}></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(212, 175, 55, 0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(212, 175, 55, 0.03) 1px, transparent 1px)`,
          backgroundSize: '100px 100px',
        }}></div>
      </div>

      {/* Floating Elements */}
      <div className={`absolute top-1/4 ${isRTL ? 'right-10' : 'left-10'} w-72 h-72 bg-gold/10 rounded-full blur-3xl animate-float`}></div>
      <div className={`absolute bottom-1/4 ${isRTL ? 'left-10' : 'right-10'} w-96 h-96 bg-navy-light/30 rounded-full blur-3xl animate-float`} style={{ animationDelay: '3s' }}></div>

      {/* Main Content - Centered */}
      <div className="relative w-full max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-32">
        <div className={`max-w-4xl mx-auto text-center space-y-6 md:space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          {/* Main Heading */}
          <div className="space-y-3 md:space-y-4">
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.2] tracking-wide">
              <span className="inline-block">{t('hero.title1')}</span>
              <span className="inline-block text-gold mx-2" 
                style={{ textShadow: '1px 1px 6px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.5)' }}>
                {t('hero.title2')}
              </span>
              <span className="inline-block">{t('hero.title3')}</span>
            </h1>
          </div>

          {/* Decorative Line */}
          <div className="flex items-center justify-center gap-3 md:gap-4">
            <div className="w-10 md:w-16 h-px bg-gradient-to-r from-transparent via-gold to-transparent"></div>
            <span 
              className="text-white text-[10px] md:text-xs tracking-[0.2em] uppercase font-light"
              style={{ textShadow: '1px 1px 6px rgba(0,0,0,0.9)' }}>
              {t('hero.subtitle')}
            </span>
            <div className="w-10 md:w-16 h-px bg-gradient-to-r from-transparent via-gold to-transparent"></div>
          </div>

          {/* Description */}
          <p className="text-sm md:text-base lg:text-lg text-white/70 leading-relaxed max-w-2xl mx-auto px-4 font-light">
            {t('hero.description')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 pt-4">
            <Link
              to="/about"
              className={`group px-5 md:px-8 py-2.5 md:py-3.5 bg-gradient-to-r from-gold to-gold-dark text-navy font-semibold rounded-lg hover:shadow-lg hover:shadow-gold/20 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2 md:gap-3 text-sm md:text-base ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              {t('hero.discoverMore')}
              <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line group-hover:${isRTL ? '-translate-x-1' : 'translate-x-1'} transition-transform`}></i>
            </Link>
            <Link
              to="/contact"
              className="px-5 md:px-8 py-2.5 md:py-3.5 border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 hover:border-gold/50 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              {t('hero.getInTouch')}
            </Link>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-12 pt-6 md:pt-8 border-t border-white/10 max-w-xl mx-auto">
            <div className="text-center">
              <div className="text-2xl md:text-3xl lg:text-4xl font-serif text-gold">8+</div>
              <div className="text-[10px] md:text-xs text-white/50 mt-1">{t('hero.portfolioCompanies')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl lg:text-4xl font-serif text-gold">UAE</div>
              <div className="text-[10px] md:text-xs text-white/50 mt-1">{t('hero.headquarters')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl lg:text-4xl font-serif text-gold">2019</div>
              <div className="text-[10px] md:text-xs text-white/50 mt-1">{t('hero.established')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/50 text-xs tracking-widest uppercase">{t('hero.scroll')}</span>
        <i className="ri-arrow-down-line text-gold text-xl"></i>
      </div>

      {/* Logo - top right corner
<div className="absolute bottom-0 right-2 z-30">
  <img 
    src="/logowhite.png" 
    alt="ALEBEL" 
    className="h-12 md:h-40 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
  />
</div> */}
    </section>
  );
}